import { Aggregation } from "./aggregation"
import { Characteristics } from "./characteristics"
import { ConditionKeys } from "./conditionKeys"
import { Filter, matchFilter } from "./filter"
import Parser from "./parser.cjs"
import { Search } from "./search"
import { Sort } from "./sort"
import { AbstractFilter } from "./types/AbstractFilter"
import { SelectProductsCommandRequest } from "./types/request"
import { Product } from "./types/response"

/* eslint-disable no-dupe-class-members */

const parse = Parser.parse

/**
 * Build a product filter
 */
export class Selection {
    private _limit: number | null
    private _offset: number | null
    private _filter: AbstractFilter | null
    private _aggregation: Aggregation | null
    private _characteristics: Characteristics | null
    private _conditionKeys: ConditionKeys | null
    private _sort: Sort | null
    private _search: Search | null
    private _with: AbstractFilter | null
    private _exactTotalCount: boolean | null

    constructor() {
        this._limit = null
        this._offset = null
        this._filter = null
        this._aggregation = null
        this._characteristics = null
        this._conditionKeys = null
        this._sort = null
        this._search = null
        this._with = null
        this._exactTotalCount = null
    }

    /**
     * Restricts the number of products which will be returned. If the limit is set to 0, no product will be returned. Default is 50.
     * @param {Number} i the maximum number of results
     * @returns this
     */
    limit(i: number): this {
        this._limit = i
        return this
    }

    /**
     * Indicates the start index. Default is 0.
     * @param {Number} i the offset
     * @returns this
     */
    offset(i: number): this {
        this._offset = i
        return this
    }

    /**
     * Getter for the filter element defining the filter criteria (product group, product IDs, characteristics etc.).
     * @return The filter
     */
    filter(): null | AbstractFilter
    /**
     * Setter for the filter element defining the filter criteria (product group, product IDs, characteristics etc.).
     * @param {Filter} f the filter to set
     * @return this
     */
    filter(f: AbstractFilter): this
    /**
     * Filter element defining the filter criteria (product group, product IDs, characteristics etc.).
     * @param {Filter} f the filter to set
     * @return filter if no arguments given or this
     */
    filter(f?: AbstractFilter): this | null | AbstractFilter {
        if (arguments.length === 0 || typeof f === "undefined") {
            return this._filter
        }

        this._filter = f
        return this
    }

    /**
     * Getter for the element specifying the characteristics for which the response should contain the aggregated
     * values.
     * @return The aggregation
     */
    aggregation(): null | Aggregation
    /**
     * Setter for the element specifying the characteristics for which the response should contain the aggregated
     * values.
     * @param {Aggregation} a the aggregation to set
     * @return this
     */
    aggregation(a: Aggregation): this
    /**
     * Element specifying the characteristics for which the response should contain the aggregated values.
     * @param {Aggregation} a the aggregation to set
     * @return aggregation if no arguments given or this
     */
    aggregation(a?: Aggregation): this | null | Aggregation {
        if (arguments.length === 0 || typeof a === "undefined") {
            return this._aggregation
        }
        this._aggregation = a
        return this
    }

    /**
     * Getter for the element specifying which characteristics and characteristic values the response should contain.
     * @return The characteristics
     */
    characteristics(): Characteristics | null
    /**
     * Setter for the element specifying which characteristics and characteristic values the response should contain.
     * @param {Characteristic} c the characteristic to set
     * @return this
     */
    characteristics(c: Characteristics): this
    /**
     * Element specifying which characteristics and characteristic values the response should contain.
     * @param {Characteristic} c the characteristic to set
     * @return aggregation if no arguments given or this
     */
    characteristics(c?: Characteristics): this | null | Characteristics {
        if (arguments.length === 0 || typeof c === "undefined") {
            return this._characteristics
        }
        this._characteristics = c
        return this
    }

    /**
     * Getter for the condition keys. If this element is specified it has to contain at least one value resp. one
     * object of type ConditionKey otherwise service responds with an bad status.
     * @return The condition keys
     */
    conditionKeys(): null | ConditionKeys
    /**
     * Setter for the condition keys. If this element is specified it has to contain at least one value resp. one
     * object of type ConditionKey otherwise service responds with an bad status.
     * @param {ConditionKeys} ck the condition keys
     * @return this
     */
    conditionKeys(ck: ConditionKeys): this
    /**
     * Condition keys. If this element is specified it has to contain at least one value resp. one object of type ConditionKey otherwise service responds with an bad status.
     * @param {ConditionKeys} ck the condition keys
     * @return condition keys if no arguments given or this
     */
    conditionKeys(ck?: ConditionKeys): this | null | ConditionKeys {
        if (arguments.length === 0 || typeof ck === "undefined") {
            return this._conditionKeys
        }
        this._conditionKeys = ck
        return this
    }

    /**
     * Getter for the sort keys. The priority of the sort keys is defined by the order in which they are provided,
     * i.e. the first sort key has the highest priority.
     * @return The sort order
     */
    sort(): null | Sort
    /**
     * Setter for the sort keys. The priority of the sort keys is defined by the order in which they are provided,
     * i.e. the first sort key has the highest priority.
     * @param {Sort} builder the sort builder
     * @return this
     */
    sort(builder: Sort): this
    /**
     * Sort keys. The priority of the sort keys is defined by the order in which they are provided, i.e. the first sort key has the highest priority.
     * @param {Sort} builder the sort builder
     * @return sort order if no arguments given or this
     */
    sort(builder?: Sort): this | null | Sort {
        if (arguments.length === 0 || typeof builder === "undefined") {
            return this._sort
        }
        this._sort = builder
        return this
    }

    /**
     * Getter for the element defining a full text search. This element cannot be used in conjunction with an
     * undetermined language (Accept-Language 'und').
     * @return The search
     */
    search(): null | Search
    /**
     * Setter for the element defining a full text search. This element cannot be used in conjunction with an
     * undetermined language (Accept-Language 'und').
     * @param {Search} s the free text search information
     * @return this
     */
    search(s: Search): this
    /**
     * Element defining a full text search. This element cannot be used in conjunction with an undetermined language (Accept-Language 'und').
     * @param {Search} s the free text search information
     * @return search if no arguments given or this
     */
    search(s?: Search): this | null | Search {
        if (arguments.length === 0 || typeof s === "undefined") {
            return this._search
        }
        this._search = s
        return this
    }

    /**
     * Getter for the optional sales context filter
     * @return The context filter
     */
    with(): null | AbstractFilter
    /**
     * Setter for the optional sales context filter
     * @param {Filter} f filter
     * @return this
     */
    with(f: AbstractFilter): this
    /**
     * Optional sales context filter
     * @param {Filter} f filter
     * @return context if no arguments given or this
     */
    with(f?: AbstractFilter): this | null | AbstractFilter {
        if (arguments.length === 0 || typeof f === "undefined") {
            return this._with
        }
        this._with = f
        return this
    }

    /**
     * Getter for the exact total count value.
     * @returns The exact total count
     */
    exactTotalCount(): null | boolean
    /**
     * Setter for the exact total count value.
     * @param {boolean} force an exact total count value (defaults to true).
     * @returns this
     */
    exactTotalCount(force: boolean): this
    /**
     * Optional force an exact total count value.
     * @param {boolean} force an exact total count value (defaults to true).
     * @returns this
     */
    exactTotalCount(force?: boolean): this | null | boolean {
        if (force === undefined) {
            return this._exactTotalCount
        }
        this._exactTotalCount = force
        return this
    }

    /**
     * Build the filter.
     * @return {Object} the final json
     */
    build(): SelectProductsCommandRequest {
        const result = {} as SelectProductsCommandRequest
        if (this._limit !== null) {
            result.limit = this._limit
        }
        if (this._offset !== null) {
            result.offset = this._offset
        }
        if (this._filter !== null) {
            if (this._with !== null) {
                result.filter = Filter.boolFilter()
                    .must(this._filter)
                    .must(this._with)
                    .build()
            } else {
                result.filter = this._filter ? this._filter.build() : undefined
            }
        } else if (this._with !== null) {
            result.filter = this._with ? this._with.build() : undefined
        }

        if (this._sort !== null) {
            result.sort = this._sort ? this._sort.build() : []
        }
        if (this._aggregation !== null) {
            result.aggregation = this._aggregation
                ? this._aggregation.build()
                : undefined
        }
        if (this._characteristics !== null) {
            result.characteristics = this._characteristics
                ? this._characteristics.build()
                : undefined
        }
        if (this._conditionKeys !== null) {
            result.date = new Date().toISOString()
            result.conditionKeys = this._conditionKeys
                ? this._conditionKeys.build()
                : undefined
        }
        if (this._search !== null) {
            result.search = this._search ? this._search.build() : undefined
        }
        if (this._exactTotalCount !== null) {
            result.exactTotalCount = this._exactTotalCount
        }

        return result
    }

    /**
     * Does the product matches this selection filter?
     * @param {Object} product
     * @param {Object} [context] optional values for aggregation replacements
     * @returns {Boolean}
     */
    test(product: Product, context?: any): boolean {
        if (!this._filter) {
            return true
        }

        let testFilters = this._filter
        // Expand filter
        if (this._aggregation && context) {
            const additionalFilter = this._aggregation.ids
                .filter((key) =>
                    Object.prototype.hasOwnProperty.call(context, key)
                )
                .map((key) =>
                    Filter.characteristicFilter().id(key).equal(context[key])
                )
            if (additionalFilter.length > 0) {
                const mergedFilters = Filter.boolFilter().must(this._filter)
                additionalFilter.forEach((filter) => mergedFilters.must(filter))
                testFilters = mergedFilters
            }
        }

        try {
            return matchFilter(testFilters, product)
        } catch (e) {
            return false
        }
    }

    /**
     * Creates a selection from an query string.
     * @param {String} query the query language string
     * @param {Object} [context] optional context to replace variables in the query
     */
    static fromQuery(query: string, context?: any): Selection {
        const rex = /{([^}]+)}/g

        const queryString = query.replace(
            rex,
            (match, variable) => context[variable]
        )

        return parse(queryString, {
            Selection,
            Filter,
            Characteristics,
            Sort,
            Aggregation
        })
    }
}
